import React, { useEffect, useMemo } from 'react';

interface ServiceSchemaProps {
  serviceName: string;
  description: string;
  serviceType: string;
  url: string;
  price?: string;
  features?: string[];
}

interface LocalBusinessSchemaProps {
  companyName: string;
  description: string;
  address?: {
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  telephone?: string;
  email?: string;
  url: string;
  logo?: string;
  socialMedia?: string[];
  openingHours?: string[];
}

export const ServiceSchema: React.FC<ServiceSchemaProps> = ({
  serviceName,
  description,
  serviceType,
  url,
  price,
  features
}) => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Service",
    "name": serviceName,
    "description": description,
    "serviceType": serviceType,
    "url": url,
    "provider": {
      "@type": "Organization",
      "name": "Cognitive Craft Hub",
      "url": "https://cognitivecrafthub.com",
      "logo": "https://cognitivecrafthub.com/images/logo.png"
    },
    ...(price && { "offers": {
      "@type": "Offer",
      "description": `${serviceName} pricing`,
      "url": url,
      "priceCurrency": "USD",
      "price": price
    }}),
    ...(features && { "additionalProperty": features.map(feature => ({
      "@type": "PropertyValue",
      "name": "Feature",
      "value": feature
    }))})
  }), [serviceName, description, serviceType, url, price, features]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = `service-schema-${serviceName.toLowerCase().replace(/\s+/g, '-')}`;
    
    // Remove existing schema if present
    const existing = document.getElementById(script.id);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(script.id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [schema, serviceName]);

  return null;
};

export const LocalBusinessSchema: React.FC<LocalBusinessSchemaProps> = ({
  companyName,
  description,
  address,
  telephone,
  email,
  url,
  logo,
  socialMedia,
  openingHours
}) => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "name": companyName,
    "description": description,
    "url": url,
    ...(logo && { "logo": logo }),
    ...(address && { "address": {
      "@type": "PostalAddress",
      ...(address.streetAddress && { "streetAddress": address.streetAddress }),
      ...(address.addressLocality && { "addressLocality": address.addressLocality }),
      ...(address.addressRegion && { "addressRegion": address.addressRegion }),
      ...(address.postalCode && { "postalCode": address.postalCode }),
      ...(address.addressCountry && { "addressCountry": address.addressCountry })
    }}),
    ...(telephone && { "telephone": telephone }),
    ...(email && { "email": email }),
    ...(socialMedia && { "sameAs": socialMedia }),
    ...(openingHours && { "openingHours": openingHours }),
    "areaServed": {
      "@type": "Country",
      "name": "United States"
    },
    "serviceArea": {
      "@type": "GeoCircle",
      "geoMidpoint": {
        "@type": "GeoCoordinates",
        "latitude": "39.8283",
        "longitude": "-98.5795"
      },
      "geoRadius": "2000000"
    }
  }), [companyName, description, url, logo, address, telephone, email, socialMedia, openingHours]);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'local-business-schema';
    
    // Remove existing schema if present
    const existing = document.getElementById(script.id);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(script.id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [schema]);

  return null;
};

export const OrganizationSchema: React.FC = () => {
  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Cognitive Craft Hub",
    "url": "https://cognitivecrafthub.com",
    "logo": "https://cognitivecrafthub.com/cch-logo.svg",
    "address": [
      {
        "@type": "PostalAddress",
        "addressLocality": "East Lansing",
        "addressRegion": "MI",
        "addressCountry": "US"
      },
      {
        "@type": "PostalAddress",
        "addressLocality": "Houston",
        "addressRegion": "TX",
        "addressCountry": "US"
      }
    ],
    "alternateName": "CCH",
    "description": "Specialized provider of AI-powered business solutions, digital marketing automation, cloud infrastructure, and custom software development services.",
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-555-0123",
      "contactType": "customer service",
      "email": "info@cognitivecrafthub.com",
      "availableLanguage": "English"
    },
    "sameAs": [
      "https://www.linkedin.com/company/cognitive-craft-hub",
      "https://twitter.com/cognitivecrafthub",
      "https://www.facebook.com/cognitivecrafthub"
    ],
    "founder": {
      "@type": "Person",
      "name": "Cognitive Craft Hub Team"
    },
    "foundingDate": "2023",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "value": "10-50"
    },
    "industry": "Information Technology",
    "knowsAbout": [
      "Artificial Intelligence",
      "Machine Learning",
      "Digital Marketing",
      "Cloud Computing",
      "Software Development",
      "Business Automation",
      "Data Analytics",
      "IoT Solutions"
    ]
  }), []);

  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema);
    script.id = 'organization-schema';
    
    // Remove existing schema if present
    const existing = document.getElementById(script.id);
    if (existing) {
      existing.remove();
    }
    
    document.head.appendChild(script);
    
    return () => {
      const scriptToRemove = document.getElementById(script.id);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [schema]);

  return null;
};